import { trackCustomAction } from '@openphone/quo-observability'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAppStore } from '@src/app/context'
import { logError } from '@src/lib/log'
import isClockSkewError from '@src/service/transport/isClockSkewError'
import { LoadingIndicator } from '@ui/LoadingIndicator'

import type UniversalLoginAppState from './UniversalLoginAppState'
import * as styles from './UniversalLoginAuthorize.css'

const UniversalLoginAuthorize = () => {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- UXP-5563 Remove legacy Toast API usage
  const { service, toast } = useAppStore()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const handleUnsuccessfulRedirect = useCallback(() => {
    service.authV2
      .checkIfAuthenticated()
      .catch(logError)
      .finally(() => {
        navigate('/login')
      })
  }, [navigate, service.authV2])

  const handleRedirectCallback = useCallback(async () => {
    try {
      const result =
        await service.authV2.auth0Client.handleRedirectCallback<UniversalLoginAppState>()

      if (result.appState) {
        const inviteToken = result.appState.inviteToken
        const referralCode = result.appState.referralCode
        const couponCode = result.appState.couponCode
        const phoneNumber = result.appState.phoneNumber
        const action = result.appState.action

        if (inviteToken) {
          runInAction(() => {
            service.authV2.inviteToken = inviteToken
          })
        }

        if (referralCode) {
          runInAction(() => {
            service.authV2.referralCode = referralCode
          })
        }

        if (couponCode) {
          await service.billing.fetchCoupon(couponCode)
        }

        if (phoneNumber) {
          // eslint-disable-next-line react-hooks/immutability -- UXP-3732 - Fix React Compiler errors
          service.authV2.phoneNumber = phoneNumber
        }

        if (action) {
          const isSignup =
            action === 'passwordless_signup' ||
            action === 'google_signup' ||
            action === 'apple_signup'
          const email = isSignup
            ? (await service.authV2.auth0Client.getUser())?.email
            : undefined

          const handlerByAction: Record<typeof action, () => void> = {
            login: () => service.analytics.workspace.login('password'),
            passwordless_login: () => service.analytics.workspace.login('passwordless'),
            google_login: () => service.analytics.workspace.login('google'),
            apple_login: () => service.analytics.workspace.login('apple'),
            passwordless_signup: () =>
              service.analytics.onceIdentified(() =>
                service.analytics.workspace.signup('passwordless', !inviteToken, email),
              ),
            google_signup: () =>
              service.analytics.onceIdentified(() =>
                service.analytics.workspace.signup('google', !inviteToken, email),
              ),
            apple_signup: () =>
              service.analytics.onceIdentified(() =>
                service.analytics.workspace.signup('apple', !inviteToken, email),
              ),
          }

          handlerByAction[action]()

          // Store pending signup tracking for login actions
          // Will be checked in onboarding to emit signup event if user is new
          // TODO: https://linear.app/openphone/issue/GATE-3297/spike-handle-gtm-sign-up-events-via-other-means
          if (
            action === 'passwordless_login' ||
            action === 'google_login' ||
            action === 'apple_login'
          ) {
            service.authV2.pendingSignupViaLogin = {
              method:
                action === 'passwordless_login'
                  ? 'passwordless'
                  : action === 'google_login'
                    ? 'google'
                    : 'apple',
              trackGTM: !inviteToken,
            }
          }

          trackCustomAction({
            type: 'universal-login-authentication',
            'activity.type': action,
          })
        }
      }

      runInAction(() => {
        service.authV2.sessionStartedOn = Date.now()
      })

      const intent = result.appState?.intent
      await service.authV2?.completeAuthentication()
      navigate(intent ?? '/')
    } catch (error) {
      logError(error)

      if (error instanceof Error) {
        trackCustomAction({
          type: 'universal-login-authorize-error',
          error: error,
        })
      }

      if (isClockSkewError(error)) {
        service.authV2.showLogoutReason('machine-clock-skew')
        toast.showError('Session expired. Please try again.')
      } else {
        toast.showError('Something went wrong. Please try again.')
      }

      handleUnsuccessfulRedirect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: Add missing dependencies or refactor effect (UXF-1509)
  }, [
    service.authV2,
    service.analytics.workspace,
    service.billing,
    navigate,
    handleUnsuccessfulRedirect,
    toast,
  ])

  useEffect(() => {
    const code = searchParams.get('code')
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (errorDescription || error) {
      toast.showError(errorDescription || error)
    }

    if (!code) {
      handleUnsuccessfulRedirect()
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    handleRedirectCallback()
  }, [
    searchParams,
    navigate,
    handleRedirectCallback,
    toast,
    service.authV2,
    handleUnsuccessfulRedirect,
  ])

  return (
    <div className={styles.root}>
      <LoadingIndicator size={30} variant="circular" />
    </div>
  )
}

export default observer(UniversalLoginAuthorize)
